import { useCallback, useMemo, useState } from 'react';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import {
  Banner,
  Button,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AddIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { FormattedMessage, injectIntl } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import TableLayoutV2 from '../../../components/TableLayoutV2';
import {
  useRepaymentAgreementsTable,
  normalizeRepaymentAgreementForTable,
} from './hooks';
import messages from './messages';
import parentMessages from '../messages';
import AddRepaymentAgreementModal from './AddRepaymentAgreementModal';
import DeleteRepaymentAgreementModal from './DeleteRepaymentAgreementModal';
import HudVoucherService from '../../../services/hudVoucherService';
import ConditionalTooltip from '../../../components/ConditionalTooltip';
import { BASE_TABLE_LAYOUT_SX } from '../constants';

/**
 * @typedef {Object} RepaymentAgreementProps
 * @property {boolean} show
 * @property {string} organizationId
 * @property {string} propertyId
 * @property {string} voucherId
 * @property {Object} voucher
 * @property {import('react-intl').IntlShape} intl
 * @property {string} voucherSubmissionStatus
 * @property {(status: string) => void} onVoucherStatusChange
 * @property {() => void} onRefreshVoucher
 * @property {(total: number) => void} onRepaymentTotalChange
 */

const ELEMENT_IDS_TO_WATCH = ['guidance-message'];

/**
 * @param {RepaymentAgreementProps} props
 */
function RepaymentAgreement({
  show,
  organizationId,
  propertyId,
  voucherId,
  voucher,
  intl,
  voucherSubmissionStatus,
  onVoucherStatusChange,
  onRefreshVoucher,
  onRepaymentTotalChange,
  isReadOnly = false,
  hasVariance = false,
  canManage,
}) {
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [selectedRepaymentRow, setSelectedRepaymentRow] = useState(null);

  const hudVoucherService = useMemo(() => new HudVoucherService(), []);

  const repaymentAgreementsQueryKey = useMemo(
    () => [
      'hudVoucherRepaymentAgreements',
      organizationId,
      propertyId,
      voucherId,
    ],
    [organizationId, propertyId, voucherId],
  );

  const voucherStatsQueryKey = useMemo(
    () => ['hudVoucherStats', organizationId, propertyId, voucherId],
    [organizationId, propertyId, voucherId],
  );
  const hudVouchersQueryKey = useMemo(
    () => ['hudVouchers', organizationId, propertyId],
    [organizationId, propertyId],
  );

  const isQueryEnabled = Boolean(
    show && organizationId && propertyId && voucherId,
  );

  const { data: repaymentResponse, isLoading: isListLoading } = useQuery(
    repaymentAgreementsQueryKey,
    () =>
      hudVoucherService.getRepaymentAgreements(
        organizationId,
        propertyId,
        voucherId,
      ),
    {
      enabled: isQueryEnabled,
      onError: () => {
        toastr.error('Error', intl.formatMessage(messages.loadError));
      },
    },
  );

  const stableEmptyAgreements = useMemo(() => [], []);
  const repaymentAgreements = useMemo(() => {
    const rowsFromApi = Array.isArray(repaymentResponse?.rows)
      ? repaymentResponse.rows
      : stableEmptyAgreements;
    if (rowsFromApi.length === 0) return stableEmptyAgreements;
    return rowsFromApi.map(normalizeRepaymentAgreementForTable);
  }, [repaymentResponse, stableEmptyAgreements]);

  const hasCarriedOverRepayments = useMemo(() => {
    if (typeof repaymentResponse?.hasCarriedOverRepayments === 'boolean') {
      return repaymentResponse.hasCarriedOverRepayments;
    }
    return repaymentAgreements.some((row) => row.isCarriedOver);
  }, [repaymentResponse, repaymentAgreements]);

  const { mutateAsync: createRepaymentAgreement, isLoading: isCreating } =
    useMutation(
      (payload) =>
        hudVoucherService.createRepaymentAgreement(
          organizationId,
          propertyId,
          voucherId,
          payload,
        ),
      {
        onSuccess: async () => {
          const previousStatus = voucherSubmissionStatus;

          toastr.success('Success', intl.formatMessage(messages.createSuccess));

          if (onRefreshVoucher) {
            await onRefreshVoucher();
          }

          if (previousStatus === 'Submitted' && onVoucherStatusChange) {
            onVoucherStatusChange('Draft');
            toastr.warning(
              intl.formatMessage(messages.voucherStatusUpdatedTitle),
              intl.formatMessage(messages.voucherReturnedToDraft),
            );
          }
        },
        onError: (error) => {
          const raw = error?.message ?? error?.error ?? '';
          const isUnitResidentRequired =
            typeof raw === 'string' &&
            (raw.includes('Unit / Resident') ||
              raw.includes('unitId') ||
              raw.includes('householdId'));
          const msg = isUnitResidentRequired
            ? intl.formatMessage(messages.unitResidentRequired)
            : raw || intl.formatMessage(messages.createError);
          toastr.error('Error', msg);
        },
      },
    );

  const { mutateAsync: deleteRepaymentAgreement, isLoading: isDeleting } =
    useMutation(
      (repaymentAgreementId) =>
        hudVoucherService.deleteRepaymentAgreement(
          organizationId,
          propertyId,
          voucherId,
          repaymentAgreementId,
        ),
      {
        onSuccess: async (data) => {
          setDeleteModalOpen(false);
          setSelectedAgreement(null);
          const previousStatus = voucherSubmissionStatus;
          toastr.success('Success', intl.formatMessage(messages.deleteSuccess));
          if (
            typeof data?.totalPart6Amount === 'number' &&
            onRepaymentTotalChange
          ) {
            onRepaymentTotalChange(data.totalPart6Amount);
          }
          if (onRefreshVoucher) {
            await onRefreshVoucher();
          }
          if (previousStatus === 'Submitted' && onVoucherStatusChange) {
            onVoucherStatusChange('Draft');
            toastr.warning(
              intl.formatMessage(messages.voucherStatusUpdatedTitle),
              intl.formatMessage(messages.voucherReturnedToDraft),
            );
          }
        },
        onError: (error) => {
          const errorMessage =
            error?.error ??
            error?.message ??
            (typeof error === 'string' ? error : null) ??
            intl.formatMessage(messages.deleteError);
          toastr.error('Error', errorMessage);
        },
      },
    );

  const { mutateAsync: modifyRepaymentAgreement, isLoading: isModifying } =
    useMutation(
      ({ voucherRepaymentRowId, payload }) =>
        hudVoucherService.modifyRepaymentAgreement(
          organizationId,
          propertyId,
          voucherId,
          voucherRepaymentRowId,
          payload,
        ),
      {
        onSuccess: async (data) => {
          const previousStatus = voucherSubmissionStatus;
          queryClient.setQueryData(
            repaymentAgreementsQueryKey,
            (previousData) => {
              if (!previousData || !Array.isArray(previousData.rows)) {
                return previousData;
              }
              const getRepaymentRowId = (row) =>
                String(row?.voucherRepaymentRowId ?? row?.id ?? '');
              const targetId = String(
                data?.updatedRow?.voucherRepaymentRowId ??
                  data?.updatedRow?.id ??
                  '',
              );
              const updatedRows = previousData.rows.map((row) =>
                getRepaymentRowId(row) === targetId && targetId !== ''
                  ? data.updatedRow
                  : row,
              );
              return {
                ...previousData,
                rows: updatedRows,
                summary: data?.updatedSummary ?? previousData.summary,
              };
            },
          );
          toastr.success('Success', intl.formatMessage(messages.modifySuccess));
          if (previousStatus === 'Submitted' && onVoucherStatusChange) {
            onVoucherStatusChange('Draft');
            toastr.warning(
              intl.formatMessage(messages.voucherStatusUpdatedTitle),
              intl.formatMessage(messages.voucherReturnedToDraft),
            );
          }
          setIsModifyModalOpen(false);
          setSelectedRepaymentRow(null);

          void Promise.allSettled([
            queryClient.invalidateQueries(hudVouchersQueryKey),
            onRefreshVoucher ? onRefreshVoucher() : Promise.resolve(),
          ]);
        },
        onError: (error) => {
          const errorMessage =
            error?.error ??
            error?.message ??
            (typeof error === 'string' ? error : null) ??
            intl.formatMessage(messages.modifyError);
          toastr.error('Error', errorMessage);
        },
      },
    );

  const voucherMonthMoment = useMemo(() => {
    const vmy = voucher?.voucherYearMonth ?? voucher?.voucherMonthYear ?? '';
    if (!vmy) return null;
    return moment(vmy, 'YYYY-MM').startOf('month');
  }, [voucher?.voucherYearMonth, voucher?.voucherMonthYear]);

  const handleAddNew = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  const handleSubmitNewAgreement = useCallback(
    async (payload) => {
      await createRepaymentAgreement(payload);
      setIsAddModalOpen(false);
    },
    [createRepaymentAgreement],
  );

  const handleDeleteClick = useCallback((agreement) => {
    setSelectedAgreement(agreement);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedAgreement?.agreementUuid) {
      deleteRepaymentAgreement(selectedAgreement.agreementUuid);
    }
  }, [selectedAgreement, deleteRepaymentAgreement]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedAgreement(null);
  }, []);

  const handleModifyClick = useCallback((row) => {
    setSelectedRepaymentRow(row);
    setIsModifyModalOpen(true);
  }, []);

  const modifyInitialValues = useMemo(() => {
    if (!selectedRepaymentRow) return null;
    const typeCode = selectedRepaymentRow.agreementTypeCode;
    const agreementType =
      typeCode === 'TENANT' ? 'T' : typeCode === 'OWNER' ? 'O' : 'N';
    const rawDate = selectedRepaymentRow.agreementDate;
    const dateMoment = rawDate ? moment(rawDate) : null;
    const agreementDateMoment =
      dateMoment && dateMoment.isValid() ? dateMoment : null;
    return {
      agreementType,
      newOrOngoing: 'ONGOING',
      householdStatus: selectedRepaymentRow.householdStatus || '',
      unitResident: '',
      agreementDate: agreementDateMoment,
      agreementAmount: selectedRepaymentRow.agreementAmount,
      beginningBalance: selectedRepaymentRow.beginningBalance,
      totalPayment: selectedRepaymentRow.totalPayment,
      agreementChangeAmount: selectedRepaymentRow.agreementChangeAmount,
      endingBalance: selectedRepaymentRow.endingBalance,
      amountRetained: selectedRepaymentRow.amountRetained,
      amountRequested: selectedRepaymentRow.requestedAmount,
      agreementId: selectedRepaymentRow.agreementId,
    };
  }, [selectedRepaymentRow]);

  const handleModifyModalClose = useCallback(() => {
    setIsModifyModalOpen(false);
    setSelectedRepaymentRow(null);
  }, []);

  const { mutateAsync: updateApprovedAmount } = useMutation(
    ({ repaymentAgreementRow, approvedAmount }) => {
      const voucherRepaymentRowId =
        repaymentAgreementRow?.voucherRepaymentRowId ??
        repaymentAgreementRow?.id;
      return hudVoucherService.updateRepaymentAgreementApprovedAmount(
        organizationId,
        propertyId,
        voucherId,
        voucherRepaymentRowId,
        approvedAmount,
      );
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(repaymentAgreementsQueryKey);
        await queryClient.invalidateQueries(voucherStatsQueryKey);
        toastr.success(
          'Success',
          intl.formatMessage(messages.updateApprovedAmountSuccess),
        );
      },
      onError: () => {
        toastr.error(
          'Error',
          intl.formatMessage(messages.updateApprovedAmountError),
        );
      },
    },
  );

  const handleSaveApprovedAmount = useCallback(
    (repaymentAgreementRow, approvedAmount) =>
      updateApprovedAmount({
        repaymentAgreementRow,
        approvedAmount,
      }),
    [updateApprovedAmount],
  );

  const {
    baseTableProps,
    name,
    allColumnsHidden,
    columnOptions,
    count,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    isLoading,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount,
  } = useRepaymentAgreementsTable({
    isReadOnly,
    repaymentAgreements,
    onDelete: handleDeleteClick,
    onModify: handleModifyClick,
    voucherSubmissionStatus,
    intl,
    hasVariance,
    onSaveApprovedAmount: handleSaveApprovedAmount,
    canManage,
  });

  if (!show) {
    return null;
  }

  return (
    <TableLayoutV2
      name={name}
      watch={ELEMENT_IDS_TO_WATCH}
      fullPage
      sx={BASE_TABLE_LAYOUT_SX}
    >
      {(height) => {
        return (
          <>
            <TableHeader
              title={<FormattedMessage {...messages.tableTitle} />}
              count={count}
              totalCount={totalCount}
              actions={
                <ConditionalTooltip
                  show={!canManage}
                  title={intl.formatMessage(parentMessages.noPermissionTooltip)}
                  placement="top"
                >
                  <Button
                    startIcon={<AddIcon />}
                    variant="shout"
                    onClick={handleAddNew}
                    disabled={!isQueryEnabled || isReadOnly || !canManage}
                  >
                    <FormattedMessage {...messages.addNewButton} />
                  </Button>
                </ConditionalTooltip>
              }
            />
            {hasCarriedOverRepayments ? (
              <Banner
                status="warning"
                message={intl.formatMessage(messages.carriedOverWarningBanner)}
              />
            ) : null}
            <Table
              {...baseTableProps}
              name={name}
              allColumnsHidden={allColumnsHidden}
              columnOptions={columnOptions}
              count={count}
              dateState={dateState}
              emptyStateMessage={baseTableProps.intl.formatMessage(
                messages.emptyStateMessage,
              )}
              filterState={filterState}
              filterTypeState={filterTypeState}
              handleColumnChange={handleColumnChange}
              handleDateSubmit={handleDateSubmit}
              handleFilterChange={handleFilterChange}
              handleFilterTypeChange={handleFilterTypeChange}
              handleSearchSubmit={handleSearchSubmit}
              handleSortChange={handleSortChange}
              headers={filteredHeaders}
              height={height}
              isLoading={isLoading || isListLoading}
              order={order}
              orderBy={orderBy}
              rows={rows}
              searchState={searchState}
              selectedColumns={selectedColumns}
              totalCount={totalCount}
            />
            <AddRepaymentAgreementModal
              open={isAddModalOpen}
              intl={intl}
              organizationId={organizationId}
              propertyId={propertyId}
              voucherId={voucherId}
              voucherMonthMoment={voucherMonthMoment}
              isSubmitting={isCreating}
              onClose={handleCloseAddModal}
              onSubmit={handleSubmitNewAgreement}
              isModifying={false}
            />
            <DeleteRepaymentAgreementModal
              open={deleteModalOpen}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              intl={intl}
              isDeleting={isDeleting}
            />
            {selectedRepaymentRow && modifyInitialValues && (
              <AddRepaymentAgreementModal
                open={isModifyModalOpen}
                intl={intl}
                organizationId={organizationId}
                propertyId={propertyId}
                voucherId={voucherId}
                voucherMonthMoment={voucherMonthMoment}
                isSubmitting={isModifying}
                onClose={handleModifyModalClose}
                onSubmit={(payload) =>
                  modifyRepaymentAgreement({
                    voucherRepaymentRowId:
                      selectedRepaymentRow.voucherRepaymentRowId,
                    payload,
                  })
                }
                isModifying
                initialValues={modifyInitialValues}
              />
            )}
          </>
        );
      }}
    </TableLayoutV2>
  );
}

export default injectIntl(RepaymentAgreement);
